import {Button, Card, Flag, Separator} from 'platform/components';
import {Box, HStack, Heading, Icon, Show, Text, VStack, type IconProps} from 'platform/foundation';

import {type ReactNode} from 'react';

import {useGetVehicleQuery} from '@dms/api/sales';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {
  getVehicleSaleTypeFlagProps,
  getVehicleSellingStateFlagProps,
  useBranches,
} from '@dms/shared';

import {composePath, EMPTY_PLACEHOLDER} from 'shared/web';

import {auctionClock} from '../../model/auctionView';
import type {AuctionMoney, DateTimeString} from '../../model/auctionView.types';
import {useAuctionFormat} from '../components/useAuctionFormat';

export function PriceBlock(props: {
  label: string;
  value: string | null;
  tax: string | null;
  testId: string;
  isLarge?: boolean;
  isStruck?: boolean;
}) {
  return (
    <VStack spacing={1} data-testid={props.testId}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      {props.isLarge ? (
        <Text size="large" alternative strikethrough={props.isStruck}>
          {props.value ?? '—'}
        </Text>
      ) : (
        <Text alternative>{props.value ?? '—'}</Text>
      )}
      <Show when={props.tax}>
        <Text size="xSmall" color="secondary">
          {props.tax}
        </Text>
      </Show>
    </VStack>
  );
}

export function StatusBlock(props: {label: string; value: string; note: string; testId: string}) {
  return (
    <VStack spacing={1} data-testid={props.testId}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      <Text alternative>{props.value}</Text>
      <Text size="xSmall" color="secondary">
        {props.note}
      </Text>
    </VStack>
  );
}

// Brokerage cases have no max buying price in the pricing API. They keep the link to the purchase case.
export function ConfirmMaxPriceButton(props: {
  purchaseCaseId: string;
  isBrokerage: boolean;
  onConfirm: () => void;
  testId?: string;
}) {
  return (
    <Button
      variant="primary"
      title={i18n.t('entity.auction.confirmation.confirmMaxBuyingPrice')}
      onClick={props.isBrokerage ? undefined : props.onConfirm}
      to={
        props.isBrokerage
          ? composePath(businessCaseRoutes.buying, {
              params: {id: props.purchaseCaseId},
              isSearchParamsPreserved: false,
            })
          : undefined
      }
      data-testid={props.testId ?? 'auction-confirmation-confirm-max-price'}
    />
  );
}

export function PartyHeading(props: {
  icon: IconProps['value'];
  role: string;
  name: string;
  meta: string;
  flag?: ReactNode;
}) {
  return (
    <HStack spacing={3} align="flex-start" justify="space-between">
      <HStack spacing={3} align="center">
        <Box padding={2} borderRadius="small" backgroundColor="palettes.purple.20.100">
          <Icon value={props.icon} color="palettes.purple.60.100" size={5} />
        </Box>
        <VStack>
          <Text size="xSmall" color="secondary">
            {props.role}
          </Text>
          <Text size="large" alternative overflowWrap="anywhere">
            {props.name}
          </Text>
          <Text size="xSmall" color="secondary">
            {props.meta}
          </Text>
        </VStack>
      </HStack>
      {props.flag}
    </HStack>
  );
}

export function Countdown(props: {
  until: DateTimeString;
  now: Date;
  caption: string;
  overdueLabel: string;
  testId: string;
}) {
  const clock = auctionClock(props.until, props.now);
  return (
    <VStack spacing={1} data-testid={props.testId}>
      <Text size="small" alternative color={clock.tone === 'overdue' ? 'danger' : 'link'}>
        {clock.tone === 'overdue'
          ? props.overdueLabel
          : // shortcut: no plural forms ("1 days"). Use count keys when a round can end within 2 days.
            i18n.t('entity.auction.confirmation.timeLeft', {days: clock.days, hours: clock.hours})}
      </Text>
      <Text size="xSmall" color="secondary">
        {props.caption}
      </Text>
    </VStack>
  );
}

export function MaxPriceCalculation(props: {
  offer: AuctionMoney | null;
  maxBuyingPrice: AuctionMoney | null;
  purchaseCaseId: string;
  isBrokerage: boolean;
  onConfirm: () => void;
}) {
  const format = useAuctionFormat();
  const show = (money: AuctionMoney | null) =>
    (money ? format.money(money) : null) ?? EMPTY_PLACEHOLDER;
  const kept =
    props.offer && props.maxBuyingPrice && props.offer.currency === props.maxBuyingPrice.currency
      ? {
          amount: (Number(props.offer.amount) - Number(props.maxBuyingPrice.amount)).toFixed(2),
          currency: props.offer.currency,
        }
      : null;

  const cell = (label: string, value: string, isResult?: boolean) => (
    <Box flex={1} minWidth={0}>
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {label}
        </Text>
        <Heading size={3} alternative color={isResult ? 'link' : 'primary'}>
          {value}
        </Heading>
      </VStack>
    </Box>
  );
  const operator = (sign: string) => (
    <Box width={10}>
      <Text size="large" color="secondary" align="center">
        {sign}
      </Text>
    </Box>
  );

  return (
    <VStack spacing={4} data-testid="auction-confirmation-calculation">
      <Box
        paddingHorizontal={5}
        paddingVertical={4}
        border="1px solid"
        borderColor="palettes.neutral.40.100"
        borderRadius="small"
        backgroundColor="palettes.neutral.10.100"
      >
        <VStack spacing={3}>
          <Heading size={5} alternative>
            {i18n.t('entity.auction.confirmation.maxPriceCalculation')}
          </Heading>
          <HStack align="flex-end">
            {cell(i18n.t('entity.auction.confirmation.dealerOffer'), show(props.offer))}
            {operator('−')}
            {cell(i18n.t('entity.auction.confirmation.amountKept'), show(kept))}
            {operator('=')}
            {cell(
              i18n.t('entity.businessCase.labels.maxBuyingPrice'),
              show(props.maxBuyingPrice),
              true
            )}
          </HStack>
        </VStack>
      </Box>
      <Separator spacing={0} />
      <HStack justify="flex-end">
        <ConfirmMaxPriceButton
          purchaseCaseId={props.purchaseCaseId}
          isBrokerage={props.isBrokerage}
          onConfirm={props.onConfirm}
          testId="auction-confirmation-calculation-confirm"
        />
      </HStack>
    </VStack>
  );
}

// The dealer sells his own stock: the seller is the branch that keeps the vehicle.
export function OwnStockSeller(props: {vehicleId: string}) {
  const format = useAuctionFormat();
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: props.vehicleId});
  const {getBranchById} = useBranches();
  const branch = vehicle ? getBranchById(vehicle.branchId) : undefined;
  const saleTypeFlag = getVehicleSaleTypeFlagProps(saleVehicle?.type);
  const sellingStateFlag = getVehicleSellingStateFlagProps(saleVehicle?.sellingState);

  return (
    <Card variant="inlineWhite" data-testid="auction-confirmation-seller">
      <VStack spacing={4}>
        <PartyHeading
          icon="communication/business"
          role={i18n.t('entity.auction.confirmation.sellerOwnStock')}
          name={branch?.marketingName ?? EMPTY_PLACEHOLDER}
          meta={[i18n.t('entity.auction.confirmation.dealerOwnedStock'), branch?.city]
            .filter(Boolean)
            .join(' · ')}
        />
        <VStack spacing={2} data-testid="auction-confirmation-availability">
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.auction.confirmation.vehicleAvailability')}
          </Text>
          <HStack spacing={2} wrap>
            <Show when={saleTypeFlag}>{(flag) => <Flag {...flag} size="small" />}</Show>
            <Show when={sellingStateFlag}>{(flag) => <Flag {...flag} size="small" />}</Show>
            <Text size="xSmall" color="secondary">
              {saleVehicle?.vehicleWarehouse?.name ?? EMPTY_PLACEHOLDER}
            </Text>
          </HStack>
        </VStack>
        <Box padding={4} borderRadius="small" backgroundColor="palettes.neutral.10.100">
          <VStack spacing={1} data-testid="auction-confirmation-stock-since">
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.auction.confirmation.stockSince')}
            </Text>
            <Text alternative>
              {vehicle?.createdAt ? format.date(vehicle.createdAt) : EMPTY_PLACEHOLDER}
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Card>
  );
}
